import './CoralSKUOverview.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Baseplate, StudRange } from '@websrc/App/Components/Baseplate/Baseplate'
import { CTA } from '@websrc/App/Components/CTA'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCoralWebSkuOverview } from '@websrc/DataMappers/mapSpot'
import React, { FC, useContext, useMemo } from 'react'

import { Arrow } from './Illustrations/Arrow'
type PropsCoralWebSkuOverview = ReturnType<typeof mapCoralWebSkuOverview>

export const CoralSKUOverview: FC<PropsCoralWebSkuOverview> = ({
  badge,
  headline,
  header_label,
  cards,
  feature_header,
  features,
  sign_up,
}) => {
  const isMobile = !useIsWindowBiggerThan(719)
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)

  const { matomoPageTemplate } = useContext(AppProviderContext)
  const studRangeImage = useMemo((): StudRange[] => {
    if (isTablet) {
      return [
        {
          snapPosition: 'top-left',

          size: { rows: 12, columns: 45 },
          offset: { top: 0, right: 0, bottom: 0, left: 3 },
        },
      ]
    } else {
      return []
    }
  }, [isTablet])

  const baseplateImageStyle = useMemo(() => {
    if (isTablet) {
      return { width: 'fit-content' }
    }
  }, [isTablet])

  const determineSize = () => {
    if (isDesktop) {
      return 'large'
    } else if (isTablet) {
      return 'medium'
    } else if (isMobile) {
      return 'small'
    } else {
      return 'large'
    }
  }

  const size = determineSize()

  return (
    <div className={'sku-overview'}>
      <div className={'sku-overview__top-wrapper'}>
        <p className={'sku-overview__badge'}>{badge}</p>
        <h2 className={'sku-overview__headline'}>{headline}</h2>
        <p className={'sku-overview__header-label'}>{header_label}</p>
      </div>
      <Baseplate studRanges={studRangeImage} style={baseplateImageStyle}>
        {/* Cards */}
        <div className={'sku-overview__cards-section-wrapper'}>
          <div className={'sku-overview__cards-section-background'} />
          <div className={'sku-overview__cards-wrapper'}>
            {cards?.map((card, index) => {
              return (
                <div className={'sku-overview__card'} key={index}>
                  <CTA className={'sku-overview__card__overlay'} cta={card.link} noText={true} />
                  <div className={'sku-overview__card__image-container'}>
                    <Asset
                      altText={card.image_alt_text}
                      className={'sku-overview__card__image'}
                      objectFit={'cover'}
                      thumbnail={card.image}
                    />
                  </div>
                  <div
                    className={`sku-overview__card__text-wrapper m--${
                      ['coral-light', 'sand', 'teal-light'][index % 3]
                    }`}
                  >
                    <p className={'sku-overview__card__label-top'}>{card.card_label_top}</p>
                    <p className={'sku-overview__card__label-bottom'}>{card.card_label_bottom}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Baseplate>
      {/* Features list */}
      <div className={'sku-overview__feature-wrapper'}>
        {/* Header with svg illustration/animation */}
        <div className={'sku-overview__features__arrows-wrapper'}>
          <Arrow direction={'right'} hasGrass={true} size={size} />
          <p className={'sku-overview__features__arrows-label'}>{feature_header}</p>
          <Arrow direction={'left'} hasGrass={false} size={size} />
        </div>
        {/* List */}
        <div className={'sku-overview__features__list'}>
          {features?.map((item, index) => (
            <div className={'sku-overview__features__item-wrapper'} key={index}>
              <p className={'sku-overview__features__item'}>{item.feature}</p>
              {index !== features.length - 1 && (
                <>
                  <div className={'sku-overview__features__square m--global-color-green'} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {sign_up && (
        <div className={'sku-overview__sign-up-wrapper'}>
          {sign_up?.button?.text && (
            <CTA
              className={'sku-overview__button'}
              cta={sign_up.button}
              onClick={() => {
                matomo.trackEvent(
                  'conversion',
                  matomoPageTemplate,
                  ['coral SKU Overview'],
                  ['coral sign up button'],
                  'button'
                )
              }}
            />
          )}
          {sign_up?.fine_copy && <p className={'sku-overview__fine-copy'}>{sign_up.fine_copy}</p>}
        </div>
      )}
    </div>
  )
}
